import type { AxiosError, AxiosInstance, AxiosRequestConfig } from 'axios';
import { openKycGateImperative } from '@/store/kycGateStore';
import type { KycRequiredBody } from './types';

/** Marks a request already replayed after approval, so a second 403 can't loop. */
const KYC_RETRY_FLAG = '__kycRetry__';

function isKycRetry(config: AxiosRequestConfig | undefined): boolean {
  return Boolean(config && (config as Record<string, unknown>)[KYC_RETRY_FLAG]);
}

/**
 * Backstop for the server-side KycGuard. If a guarded action returns
 * 403 `{ code: 'KYC_REQUIRED', pendingFlows: [...] }`, open the KYC gate for
 * the first pending flow and wait for the user to verify.
 *
 * If KYC is approved while the gate is open, the original request is replayed
 * transparently — the caller's promise resolves with the retried response, so
 * the action that tripped the gate just continues. If the user dismisses the
 * gate instead, the original 403 propagates as before.
 *
 * Returns a teardown function (useful for tests / HMR).
 */
export function installKycInterceptor(instance: AxiosInstance): () => void {
  const id = instance.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
      const body = error.response?.data as KycRequiredBody | undefined;
      const config = error.config;
      if (error.response?.status === 403 && body?.code === 'KYC_REQUIRED' && config && !isKycRetry(config)) {
        const flowKey = body.pendingFlows?.[0]?.flowKey;
        if (flowKey) {
          const outcome = await openKycGateImperative(flowKey);
          if (outcome === 'approved') {
            return instance.request({ ...config, [KYC_RETRY_FLAG]: true } as AxiosRequestConfig);
          }
        }
      }
      throw error;
    },
  );
  return () => instance.interceptors.response.eject(id);
}
